import React, { useEffect, useMemo, useState } from 'react';
import {
    BookOpen,
    Check,
    ChevronDown,
    ChevronUp,
    Clapperboard,
    FileText,
    ImageIcon,
    Settings2,
    Sparkles,
    type LucideIcon,
    Wand2,
    X
} from 'lucide-react';
import { STYLE_FACETS } from '../../../constants/tags';
import {
    briefService,
    type AIBriefGenerationRequest,
    type AIBriefGenerationResponse
} from '../../../services/briefService';
import { useToast } from '../../UI/Toast';

interface BriefAiWizardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete: (generatedData: AIBriefGenerationResponse) => void;
    zIndex?: number;
}

type WorkTypeId = 'video' | 'image' | 'copy' | 'workflow' | 'training';

type ExamplePreset = AIBriefGenerationRequest & {
    label: string;
    summary: string;
};

type WorkTypeOption = {
    id: WorkTypeId;
    title: string;
    description: string;
    icon: LucideIcon;
    coreGoalPlaceholder: string;
    coreGoalExamples: string[];
    defaultDeliverables: string[];
    contextPlaceholder: string;
};

const WORK_TYPE_OPTIONS: WorkTypeOption[] = [
    {
        id: 'video',
        title: '视频',
        description: '广告片、宣传片、短剧、MV',
        icon: Clapperboard,
        coreGoalPlaceholder: '例如：我想做一条 20 秒的护肤品短视频，用于抖音投放，突出清爽补水。',
        coreGoalExamples: [
            '我想做一条 20 秒的护肤品短视频，用于抖音投放，突出清爽补水。',
            '我想做一个科技品牌发布会的开场视频，气质偏未来感和高端。'
        ],
        defaultDeliverables: ['1 条短视频', '可商用终稿文件'],
        contextPlaceholder: '例如：用于抖音投放，面向 20-30 岁女性；也会同步用于品牌官网。'
    },
    {
        id: 'image',
        title: '图片',
        description: '海报、KV、产品图、角色设定',
        icon: ImageIcon,
        coreGoalPlaceholder: '例如：我想做 3 张小红书海报，用于新品上线种草，风格高级干净。',
        coreGoalExamples: [
            '我想做 3 张小红书海报，用于新品上线种草，风格高级干净。',
            '我想做一组 AI 产品效果图，用于官网首屏和详情页展示。'
        ],
        defaultDeliverables: ['3 张海报/KV', '可商用终稿文件'],
        contextPlaceholder: '例如：用于小红书种草，面向年轻女性；也会用于社群和详情页。'
    },
    {
        id: 'copy',
        title: '文案/脚本',
        description: '广告文案、脚本、分镜、播稿',
        icon: FileText,
        coreGoalPlaceholder: '例如：我想写一支 30 秒广告片脚本，突出年轻、轻松、有记忆点。',
        coreGoalExamples: [
            '我想写一支 30 秒广告片脚本，突出年轻、轻松、有记忆点。',
            '我想做一份产品介绍短视频的分镜脚本，便于后续交给创作者执行。'
        ],
        defaultDeliverables: ['1 份脚本/分镜'],
        contextPlaceholder: '例如：用于品牌宣传视频，面向年轻用户，语气希望自然有记忆点。'
    },
    {
        id: 'workflow',
        title: '工作流搭建',
        description: '自动化流程、团队提效、内部工具',
        icon: Settings2,
        coreGoalPlaceholder: '例如：我想搭一套适合运营团队日常出图的 AI 工作流，提升出图效率。',
        coreGoalExamples: [
            '我想搭一套适合运营团队日常出图的 AI 工作流，提升出图效率。',
            '我想做一个适合内部重复使用的内容生产流程，降低重复劳动。'
        ],
        defaultDeliverables: ['1 套工作流方案', '操作说明文档'],
        contextPlaceholder: '例如：用于电商运营团队日常出图，希望新同事也能快速上手。'
    },
    {
        id: 'training',
        title: 'AI 培训',
        description: '培训分享、内训、案例讲解',
        icon: BookOpen,
        coreGoalPlaceholder: '例如：我想做一次面向市场团队的 AI 培训，帮助大家掌握常用提效方法。',
        coreGoalExamples: [
            '我想做一次面向市场团队的 AI 培训，帮助大家掌握常用提效方法。',
            '我想做一场结合业务案例的 AI 内训，让团队知道怎么真正落地。'
        ],
        defaultDeliverables: ['1 次 AI 培训分享', '1 份培训材料'],
        contextPlaceholder: '例如：用于公司内部培训，参与者主要是市场、运营和内容团队。'
    }
];

const QUICK_EXAMPLES: ExamplePreset[] = [
    {
        label: '品牌短视频',
        summary: '一条适合投放的品牌短视频',
        workType: 'video',
        coreGoal: '我想做一条 20 秒的护肤品品牌短视频，用于抖音投放，突出清爽补水和夏日感。',
        contextAndAudience: '用于抖音和小红书投放，面向 20-30 岁关注护肤的年轻女性。',
        selectedStyleFacets: ['sf_cinematic', 'sf_brand'],
        expectedDeliverables: ['1 条短视频', '可商用终稿文件'],
        mustInclude: '必须带品牌 Logo、产品瓶身特写和一句核心卖点文案。',
        referenceNotes: '整体气质偏高级、干净，不要太夸张的网感特效。'
    },
    {
        label: '小红书海报',
        summary: '一组适合预热种草的海报',
        workType: 'image',
        coreGoal: '我想做 3 张小红书新品预热海报，用于种草预热，突出质感和少女感。',
        contextAndAudience: '用于小红书和品牌私域社群，面向 18-28 岁女性用户。',
        selectedStyleFacets: ['sf_brand', 'sf_minimal'],
        expectedDeliverables: ['3 张海报/KV', '可商用终稿文件'],
        mustInclude: '必须包含产品正面图、品牌名和新品上市信息。',
        referenceNotes: '偏干净留白，不要太电商促销风。'
    },
    {
        label: '工作流搭建',
        summary: '一套能让团队反复使用的流程',
        workType: 'workflow',
        coreGoal: '我想搭一套适合运营团队日常出图的 AI 工作流，提升出图效率并减少重复劳动。',
        contextAndAudience: '用于电商运营团队日常出图，希望流程稳定、可重复使用。',
        selectedStyleFacets: ['sf_brand'],
        expectedDeliverables: ['1 套工作流方案', '操作说明文档'],
        mustInclude: '需要支持替换不同产品素材，并尽量保持背景和画面风格稳定。',
        referenceNotes: '优先考虑稳定性和可复用性，最好带简单上手说明。'
    },
    {
        label: 'AI 培训',
        summary: '一次贴近业务的 AI 内训',
        workType: 'training',
        coreGoal: '我想做一次面向市场和运营团队的 AI 培训，帮助大家掌握常用提效方法并能马上上手。',
        contextAndAudience: '用于公司内部培训，参与者主要是市场、运营和内容同事。',
        selectedStyleFacets: [],
        expectedDeliverables: ['1 次 AI 培训分享', '1 份培训材料'],
        mustInclude: '希望结合我们的业务场景，最好有几个现场就能复用的案例。',
        referenceNotes: '内容尽量实用，不要太学术，也不要太泛。'
    }
];

const CONTEXT_CHIPS = [
    '抖音投放',
    '小红书种草',
    '品牌官网',
    '电商详情页',
    '内部培训',
    '内部提案'
];

const COMMON_DELIVERABLE_OPTIONS = ['可商用终稿文件', '工程源文件', '操作说明文档'];

const DELIVERABLE_OPTIONS_BY_TYPE: Record<WorkTypeId, string[]> = {
    video: ['1 条短视频', '分镜脚本'],
    image: ['3 张海报/KV', '1 套角色/场景设定'],
    copy: ['1 份脚本/分镜', '1 套文案内容'],
    workflow: ['1 套工作流方案', '操作说明文档'],
    training: ['1 次 AI 培训分享', '1 份培训材料'],
};

const STYLE_HINTS: Record<string, string> = {
    sf_realistic: '真实质感',
    sf_anime: '动漫感',
    sf_chinese: '东方审美',
    sf_scifi: '未来感',
    sf_minimal: '干净克制',
    sf_cinematic: '广告/预告片感',
    sf_brand: '适合商业品牌',
    sf_watercolor: '柔和手绘',
    sf_pixel: '像素游戏感',
    sf_cyberpunk: '霓虹都市感'
};

const getInitialFormData = (): AIBriefGenerationRequest => ({
    coreGoal: '',
    contextAndAudience: '',
    workType: '',
    selectedStyleFacets: [],
    expectedDeliverables: [],
    mustInclude: '',
    referenceNotes: ''
});

const buildVisibleExamples = (workType: string | undefined): ExamplePreset[] => {
    if (!workType) {
        return QUICK_EXAMPLES.slice(0, 3);
    }

    const matched = QUICK_EXAMPLES.filter((item) => item.workType === workType);
    return matched.length > 0 ? matched.slice(0, 2) : QUICK_EXAMPLES.slice(0, 2);
};

const buildVisibleDeliverables = (workType: string | undefined): string[] => {
    if (!workType || !(workType in DELIVERABLE_OPTIONS_BY_TYPE)) {
        return ['1 条短视频', '3 张海报/KV', '1 份脚本/分镜', '1 套工作流方案', '1 次 AI 培训分享'];
    }

    return Array.from(new Set([
        ...DELIVERABLE_OPTIONS_BY_TYPE[workType as WorkTypeId],
        ...COMMON_DELIVERABLE_OPTIONS
    ]));
};

const BriefAiWizardModal: React.FC<BriefAiWizardModalProps> = ({
    isOpen,
    onClose,
    onComplete,
    zIndex = 70
}) => {
    const [formData, setFormData] = useState<AIBriefGenerationRequest>(getInitialFormData);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const { showToast } = useToast();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setFormData(getInitialFormData());
        setIsGenerating(false);
        setShowAdvanced(false);
    }, [isOpen]);

    const activeWorkType = useMemo(
        () => WORK_TYPE_OPTIONS.find((option) => option.id === formData.workType),
        [formData.workType]
    );

    const visibleExamples = useMemo(
        () => buildVisibleExamples(formData.workType),
        [formData.workType]
    );

    const visibleDeliverables = useMemo(
        () => buildVisibleDeliverables(formData.workType),
        [formData.workType]
    );

    if (!isOpen) {
        return null;
    }

    const setField = <K extends keyof AIBriefGenerationRequest>(key: K, value: AIBriefGenerationRequest[K]) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleWorkTypeSelect = (workType: WorkTypeId) => {
        const option = WORK_TYPE_OPTIONS.find((item) => item.id === workType);
        setFormData((prev) => ({
            ...prev,
            workType,
            expectedDeliverables: prev.expectedDeliverables.length > 0
                ? prev.expectedDeliverables
                : (option?.defaultDeliverables || [])
        }));
    };

    const applyQuickExample = (example: ExamplePreset) => {
        setFormData({
            coreGoal: example.coreGoal,
            contextAndAudience: example.contextAndAudience || '',
            workType: example.workType || '',
            selectedStyleFacets: example.selectedStyleFacets || [],
            expectedDeliverables: example.expectedDeliverables,
            mustInclude: example.mustInclude || '',
            referenceNotes: example.referenceNotes || ''
        });
        setShowAdvanced(Boolean(example.mustInclude || example.referenceNotes));
    };

    const toggleStyleFacet = (facetId: string) => {
        setFormData((prev) => ({
            ...prev,
            selectedStyleFacets: prev.selectedStyleFacets?.includes(facetId)
                ? prev.selectedStyleFacets.filter((id) => id !== facetId)
                : [...(prev.selectedStyleFacets || []), facetId]
        }));
    };

    const toggleDeliverable = (option: string) => {
        setFormData((prev) => ({
            ...prev,
            expectedDeliverables: prev.expectedDeliverables.includes(option)
                ? prev.expectedDeliverables.filter((item) => item !== option)
                : [...prev.expectedDeliverables, option]
        }));
    };

    const appendContextChip = (chip: string) => {
        setFormData((prev) => {
            if ((prev.contextAndAudience || '').includes(chip)) {
                return prev;
            }

            return {
                ...prev,
                contextAndAudience: prev.contextAndAudience?.trim()
                    ? `${prev.contextAndAudience.trim()}；${chip}`
                    : chip
            };
        });
    };

    const applyCoreGoalExample = (example: string) => {
        setField('coreGoal', example);
    };

    const handleSubmit = async () => {
        if (!formData.workType) {
            showToast('先选一个大致类型', 'error');
            return;
        }

        if (!formData.coreGoal.trim()) {
            showToast('请先描述你想做什么', 'error');
            return;
        }

        const deliverables = formData.expectedDeliverables.length > 0
            ? formData.expectedDeliverables
            : (activeWorkType?.defaultDeliverables || []);

        if (deliverables.length === 0) {
            showToast('请至少选择一种交付内容', 'error');
            return;
        }

        setIsGenerating(true);
        try {
            const response = await briefService.generateAIBrief({
                coreGoal: formData.coreGoal.trim(),
                contextAndAudience: formData.contextAndAudience?.trim(),
                workType: formData.workType,
                selectedStyleFacets: formData.selectedStyleFacets || [],
                expectedDeliverables: deliverables,
                mustInclude: formData.mustInclude?.trim(),
                referenceNotes: formData.referenceNotes?.trim()
            });

            const payload = response.data;

            if (response.success && payload?.success && payload.data) {
                onComplete(payload.data);
                showToast('AI 已成功为你生成需求初稿', 'success');
                onClose();
                return;
            }

            showToast(payload?.error || response.error || '生成失败，请稍后重试', 'error');
        } catch (error) {
            console.error('AI brief generation error:', error);
            showToast('生成过程中发生错误', 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex }}>
            <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-[920px] max-h-[88vh] overflow-hidden rounded-[32px] border border-[#e9e2d6] bg-[#faf8f3] shadow-[0_28px_90px_rgba(15,23,42,0.18)] flex flex-col animate-scale-in">
                <div className="px-6 md:px-7 py-5 border-b border-[#ece3d7] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(250,248,243,0.82))] flex items-start justify-between">
                    <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-[#2351f5] text-white flex items-center justify-center shadow-[0_14px_30px_rgba(37,81,245,0.24)] shrink-0">
                            <Sparkles className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-[30px] leading-none font-bold text-[#1f2937]">AI 辅助生成需求</h2>
                            <p className="text-sm text-[#6b7280] mt-2">
                                先选类型，再写一句核心诉求。其他信息都可以按需补充。
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-10 h-10 rounded-full hover:bg-white/80 flex items-center justify-center transition-colors shrink-0"
                    >
                        <X className="w-5 h-5 text-[#9ca3af]" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 md:px-7 py-5 space-y-5">
                    <section className="rounded-[26px] border border-[#ebe4d8] bg-white p-5 md:p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <span className="w-7 h-7 rounded-full bg-[#eef3ff] text-[#2351f5] text-sm font-semibold flex items-center justify-center">1</span>
                            <div>
                                <h3 className="text-base font-bold text-[#111827]">先选类型</h3>
                                <p className="text-sm text-[#6b7280]">选一个最接近的方向就行，后面 AI 会按这个语境帮你整理。</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {WORK_TYPE_OPTIONS.map((option) => {
                                const selected = formData.workType === option.id;
                                const Icon = option.icon;

                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        onClick={() => handleWorkTypeSelect(option.id)}
                                        className={`text-left rounded-[22px] border px-4 py-4 transition-all ${selected
                                            ? 'border-[#a9bfff] bg-[#f4f7ff] shadow-[0_10px_24px_rgba(59,91,219,0.08)]'
                                            : 'border-[#ece5d9] bg-[#fffdfa] hover:border-[#d8d2c6] hover:bg-white'
                                            }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${selected ? 'bg-[#2351f5] text-white' : 'bg-[#f4f4f5] text-[#6b7280]'}`}>
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            {selected && (
                                                <div className="w-5 h-5 rounded-full bg-[#2351f5] text-white flex items-center justify-center shrink-0">
                                                    <Check className="w-3 h-3" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-3">
                                            <div className="text-sm font-semibold text-[#111827]">{option.title}</div>
                                            <div className="mt-1 text-sm text-[#6b7280]">{option.description}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="pt-1">
                            <div className="text-sm font-semibold text-[#111827] mb-2">参考示例</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {visibleExamples.map((example) => (
                                    <button
                                        key={example.label}
                                        type="button"
                                        onClick={() => applyQuickExample(example)}
                                        className="text-left rounded-[20px] border border-[#ece5d9] bg-[#fffdfa] px-4 py-4 hover:border-[#bfd0ff] hover:bg-[#fbfcff] transition-all"
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-sm font-semibold text-[#111827]">{example.label}</span>
                                            <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#eef3ff] text-[#2351f5]">带入示例</span>
                                        </div>
                                        <p className="mt-2 text-sm leading-6 text-[#6b7280]">{example.summary}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="rounded-[26px] border border-[#ebe4d8] bg-white p-5 md:p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <span className="w-7 h-7 rounded-full bg-[#eef3ff] text-[#2351f5] text-sm font-semibold flex items-center justify-center">2</span>
                            <div>
                                <h3 className="text-base font-bold text-[#111827]">一句话说清你想做什么</h3>
                                <p className="text-sm text-[#6b7280]">可以先写目标和场景，细节不完整也没关系。</p>
                            </div>
                        </div>

                        <div className="rounded-[22px] border border-[#ebe5d9] bg-[#fcfbf8] p-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                                <label className="text-sm font-semibold text-[#111827]">
                                    核心需求 <span className="text-[#ef4444]">*</span>
                                </label>
                                {activeWorkType && (
                                    <span className="text-[11px] px-2.5 py-1 rounded-full bg-white text-[#6b7280] border border-[#ebe5d9]">
                                        {activeWorkType.title}
                                    </span>
                                )}
                            </div>
                            <textarea
                                value={formData.coreGoal}
                                onChange={(e) => setField('coreGoal', e.target.value)}
                                className="w-full h-32 rounded-[18px] border border-[#e3dccf] bg-white px-4 py-3 text-base text-[#1f2937] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#2351f5]/15 focus:border-[#2351f5] resize-none"
                                placeholder={activeWorkType?.coreGoalPlaceholder || '例如：我想做一条品牌短视频，用于新品预热。'}
                            />
                            <div className="flex flex-wrap gap-2 mt-3">
                                {(activeWorkType?.coreGoalExamples || QUICK_EXAMPLES.slice(0, 2).map((item) => item.coreGoal)).map((example) => (
                                    <button
                                        key={example}
                                        type="button"
                                        onClick={() => applyCoreGoalExample(example)}
                                        className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#e5dece] bg-white text-[#6b7280] hover:border-[#bfd0ff] hover:text-[#2351f5] transition-colors"
                                    >
                                        {example.length > 20 ? `${example.slice(0, 20)}...` : example}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-[1.1fr_0.9fr] gap-4">
                            <div className="rounded-[22px] border border-[#ebe5d9] bg-[#fcfbf8] p-4 space-y-3">
                                <div>
                                    <div className="text-sm font-semibold text-[#111827]">使用场景 / 受众（选填）</div>
                                    <div className="text-sm text-[#6b7280] mt-1">不知道怎么写时，先点几个常用场景。</div>
                                </div>
                                <textarea
                                    value={formData.contextAndAudience}
                                    onChange={(e) => setField('contextAndAudience', e.target.value)}
                                    className="w-full h-24 rounded-[18px] border border-[#e3dccf] bg-white px-4 py-3 text-sm text-[#1f2937] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#2351f5]/15 focus:border-[#2351f5] resize-none"
                                    placeholder={activeWorkType?.contextPlaceholder || '例如：用于品牌官网、投放或内部提案。'}
                                />
                                <div className="flex flex-wrap gap-2">
                                    {CONTEXT_CHIPS.map((chip) => (
                                        <button
                                            key={chip}
                                            type="button"
                                            onClick={() => appendContextChip(chip)}
                                            className="px-3 py-1.5 rounded-full text-xs font-medium border border-[#e5dece] bg-white text-[#6b7280] hover:border-[#bfd0ff] hover:text-[#2351f5] transition-colors"
                                        >
                                            {chip}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="rounded-[22px] border border-[#ebe5d9] bg-[#fcfbf8] p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-semibold text-[#111827]">
                                            希望拿到什么 <span className="text-[#ef4444]">*</span>
                                        </div>
                                        <div className="text-sm text-[#6b7280] mt-1">默认帮你带一些常见交付，也可以自己点选。</div>
                                    </div>
                                    <span className="text-[11px] text-[#9ca3af]">{formData.expectedDeliverables.length} 已选</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {visibleDeliverables.map((option) => {
                                        const selected = formData.expectedDeliverables.includes(option);
                                        return (
                                            <button
                                                key={option}
                                                type="button"
                                                onClick={() => toggleDeliverable(option)}
                                                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-all ${selected
                                                    ? 'border-[#2351f5] bg-[#eef3ff] text-[#2351f5]'
                                                    : 'border-[#e5dece] bg-white text-[#6b7280] hover:border-[#bfd0ff] hover:text-[#2351f5]'
                                                    }`}
                                            >
                                                {option}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-[26px] border border-[#ebe4d8] bg-white p-5 md:p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <span className="w-7 h-7 rounded-full bg-[#eef3ff] text-[#2351f5] text-sm font-semibold flex items-center justify-center">3</span>
                            <div>
                                <h3 className="text-base font-bold text-[#111827]">可选补充</h3>
                                <p className="text-sm text-[#6b7280]">有就补，没有也可以先生成。</p>
                            </div>
                        </div>

                        <div className="rounded-[22px] border border-[#ebe5d9] bg-[#fcfbf8] p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm font-semibold text-[#111827]">风格倾向（如适用）</div>
                                    <div className="text-sm text-[#6b7280] mt-1">选 1-2 个最接近的就够了。</div>
                                </div>
                                <span className="text-[11px] text-[#9ca3af]">{formData.selectedStyleFacets?.length || 0} 已选</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {STYLE_FACETS.map((facet) => {
                                    const selected = formData.selectedStyleFacets?.includes(facet.id);
                                    return (
                                        <button
                                            key={facet.id}
                                            type="button"
                                            onClick={() => toggleStyleFacet(facet.id)}
                                            className={`px-3.5 py-2 rounded-full text-sm border transition-all ${selected
                                                ? 'border-[#2351f5] bg-[#eef3ff] text-[#2351f5]'
                                                : 'border-[#e5dece] bg-white text-[#6b7280] hover:border-[#bfd0ff] hover:text-[#2351f5]'
                                                }`}
                                        >
                                            <span className="font-medium">{facet.name}</span>
                                            <span className="ml-1 text-xs opacity-70">{STYLE_HINTS[facet.id] || ''}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="rounded-[22px] border border-dashed border-[#ddd4c5] bg-[#fcfbf8] overflow-hidden">
                            <button
                                type="button"
                                onClick={() => setShowAdvanced((prev) => !prev)}
                                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/70 transition-colors"
                            >
                                <div>
                                    <div className="text-sm font-semibold text-[#111827]">更多要求（选填）</div>
                                    <div className="text-sm text-[#6b7280] mt-1">比如必须出现什么、希望避免什么、参考什么气质。</div>
                                </div>
                                {showAdvanced ? (
                                    <ChevronUp className="w-4 h-4 text-[#9ca3af] shrink-0" />
                                ) : (
                                    <ChevronDown className="w-4 h-4 text-[#9ca3af] shrink-0" />
                                )}
                            </button>

                            {showAdvanced && (
                                <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <textarea
                                        value={formData.mustInclude}
                                        onChange={(e) => setField('mustInclude', e.target.value)}
                                        className="w-full h-24 rounded-[18px] border border-[#e3dccf] bg-white px-4 py-3 text-sm text-[#1f2937] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#2351f5]/15 focus:border-[#2351f5] resize-none"
                                        placeholder="必须包含什么：例如品牌 Logo、产品主图、某个角色、主色调。"
                                    />
                                    <textarea
                                        value={formData.referenceNotes}
                                        onChange={(e) => setField('referenceNotes', e.target.value)}
                                        className="w-full h-24 rounded-[18px] border border-[#e3dccf] bg-white px-4 py-3 text-sm text-[#1f2937] leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#2351f5]/15 focus:border-[#2351f5] resize-none"
                                        placeholder="补充说明：例如参考气质、禁忌项、想要更克制或更活泼。"
                                    />
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                <div className="px-5 md:px-7 py-4 border-t border-[#ece3d7] bg-white/90 flex items-center justify-between gap-4">
                    <p className="text-sm text-[#6b7280] hidden md:block">
                        AI 会先帮你生成一版清晰的专业需求，不完整的地方会补上待确认项。
                    </p>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isGenerating || !formData.workType || !formData.coreGoal.trim()}
                        className={`ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-[18px] text-sm font-semibold transition-all ${isGenerating || !formData.workType || !formData.coreGoal.trim()
                            ? 'bg-[#f3f4f6] text-[#9ca3af] cursor-not-allowed'
                            : 'bg-[#2351f5] text-white hover:bg-[#1b44d6] shadow-[0_14px_30px_rgba(37,81,245,0.24)]'
                            }`}
                    >
                        {isGenerating ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                AI 正在整理...
                            </>
                        ) : (
                            <>
                                <Wand2 className="w-4 h-4" />
                                生成专业需求
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BriefAiWizardModal;
